import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CreditPackageCard } from '@/components/credits/CreditPackageCard';
import { CustomCreditPackage } from '@/components/credits/CustomCreditPackage';
import { Check, Info, CreditCard, Shield, DollarSign, Coins } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useCredits } from '@/hooks/useCredits';
import { CreditPersistenceService } from '@/services/creditPersistenceService';
import { CreditDebugPanel } from '@/components/credits/CreditDebugPanel';

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  popular?: boolean;
  description?: string;
}

export const ModernCreditsTab = () => {
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [customCredits, setCustomCredits] = useState(100);
  const [activeTab, setActiveTab] = useState('purchase');
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Use centralized credit management
  const { balance: credits, isLoading, refetch } = useCredits();
  
  // Credit package options
  const creditPackages: CreditPackage[] = [
    {
      id: 'starter',
      name: 'Starter',
      credits: 100,
      price: 10,
      description: 'Basic package for occasional use'
    },
    {
      id: 'pro',
      name: 'Professional',
      credits: 500,
      price: 45,
      popular: true,
      description: 'Most popular choice for regular traders'
    },
    {
      id: 'premium',
      name: 'Premium',
      credits: 1000,
      price: 80,
      description: 'Best value for power users'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      credits: 5000,
      price: 350,
      description: 'For institutional investors and professionals'
    }
  ];
  
  // Mock transaction history
  const transactions = [
    { id: '1', date: '2025-04-22', type: 'Purchase', amount: 500, price: 45.00 },
    { id: '2', date: '2025-04-15', type: 'Usage', amount: -50, details: 'Trading Strategy Optimization' },
    { id: '3', date: '2025-04-10', type: 'Usage', amount: -75, details: 'Market Analysis (SPY, QQQ)' },
    { id: '4', date: '2025-03-29', type: 'Purchase', amount: 100, price: 10.00 },
    { id: '5', date: '2025-03-20', type: 'Usage', amount: -25, details: 'Trading Strategy Creation' },
  ];
  
  const handlePurchase = async () => {
    if (!selectedPackage) {
      return;
    }

    setIsPurchasing(true);
    
    try {
      // Get current user session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session?.user) throw new Error('User not authenticated');

      const userId = session.user.id;
      
      // Get credit amount from selected package or custom input
      let creditAmount = 0;
      let purchaseDescription = '';
      let packagePrice = 0;
      
      if (selectedPackage === 'custom') {
        creditAmount = customCredits;
        purchaseDescription = 'Custom Package Purchase (Dev Mode)';
        packagePrice = customCredits * 0.1; // $0.10 per credit for custom
      } else {
        const selectedPkg = creditPackages.find(pkg => pkg.id === selectedPackage);
        if (!selectedPkg) throw new Error('Invalid package selection');
        creditAmount = selectedPkg.credits;
        purchaseDescription = `${selectedPkg.name} Package Purchase (Dev Mode)`;
        packagePrice = selectedPkg.price;
      }

      if (creditAmount <= 0) {
        throw new Error('Invalid credit amount');
      }

      // Get current balance
      const currentBalanceResult = await CreditPersistenceService.getUserBalance(userId);
      if (!currentBalanceResult.success) {
        throw new Error(currentBalanceResult.error || 'Failed to get current balance');
      }

      const currentBalance = currentBalanceResult.newBalance || 0;
      const newBalance = currentBalance + creditAmount;

      // Update balance using robust persistence service
      const updateResult = await CreditPersistenceService.updateUserBalance(userId, newBalance);
      if (!updateResult.success) {
        throw new Error(updateResult.error || 'Failed to update balance');
      }

      // Record the transaction using robust persistence service
      const transactionResult = await CreditPersistenceService.recordTransaction(
        userId,
        'purchase',
        creditAmount,
        purchaseDescription,
        { 
          dev_mode: true,
          package_id: selectedPackage,
          package_price: packagePrice
        }
      );

      if (!transactionResult.success) {
        console.warn('Transaction recording failed:', transactionResult.error);
        // Don't fail the whole operation for transaction recording
      }
      
      // Trigger UI updates
      refetch();
      
      // Verify the update was successful
      setTimeout(async () => {
        const verificationResult = await CreditPersistenceService.verifyBalance(userId);
        if (verificationResult.isConsistent && verificationResult.actualBalance >= newBalance) {
          console.log('✅ Credit purchase verified successfully');
        } else {
          console.warn('⚠️ Credit balance verification failed:', verificationResult);
          // Force another refetch to ensure UI consistency
          refetch();
        }
      }, 500);
      
      // 5. Dispatch the update event for other components
      window.dispatchEvent(new CustomEvent('creditBalanceUpdated', {
        detail: { newBalance }
      }));
      
      // 6. Show success message
      console.log(`Development Mode: Added ${creditAmount.toLocaleString()} credits to account`);
      
      // Reset selection after successful "purchase"
      setSelectedPackage(null);
      setCustomCredits(100);
      setShowConfirmation(false);
      
    } catch (error) {
      console.error('Purchase failed:', error);
      // Could add toast notification here if available
    } finally {
      setIsPurchasing(false);
    }
  };

  const getOrderDetails = () => {
    if (!selectedPackage) return null;
    
    if (selectedPackage === 'custom') {
      return {
        name: 'Custom Package',
        credits: customCredits,
        price: customCredits * 0.1,
        description: 'Custom credit amount tailored to your needs'
      };
    }
    
    return creditPackages.find(pkg => pkg.id === selectedPackage);
  };

  const orderDetails = getOrderDetails();
  
  return (
    <div className="p-0">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-slate-100">Credits & Billing</CardTitle>
            <CardDescription className="text-slate-400">
              Manage your Stewie Trader credits and payment history
            </CardDescription>
          </div>
          <div className="text-right">
            <div className="font-medium text-2xl text-slate-100">
              {isLoading ? '...' : credits.toLocaleString()}
            </div>
            <div className="text-xs text-slate-400">Available Credits</div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6 pt-0">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-700">
            <TabsTrigger value="purchase" className="data-[state=active]:bg-slate-800 data-[state=active]:text-slate-50 text-slate-300">Purchase Credits</TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-slate-800 data-[state=active]:text-slate-50 text-slate-300">Transaction History</TabsTrigger>
          </TabsList>
          
          <TabsContent value="purchase" className="space-y-6">
            {/* Add debug panel in development */}
            {process.env.NODE_ENV === 'development' && (
              <CreditDebugPanel />
            )}
            
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium mb-2 text-slate-200">Credit Packages</h3>
                <p className="text-sm text-slate-400 mb-4">
                  Choose a package or create a custom amount below.
                </p>
              </div>
              
              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
                {creditPackages.map((pkg) => (
                  <div 
                    key={pkg.id}
                    className={`relative rounded-lg border ${
                      selectedPackage === pkg.id 
                        ? 'border-blue-500 ring-2 ring-blue-500/30 bg-blue-600/10' 
                        : 'border-slate-700 hover:border-blue-500/50 bg-slate-800/50'
                    } p-4 transition-all cursor-pointer`}
                    onClick={() => setSelectedPackage(pkg.id)}
                  >
                    {pkg.popular && (
                      <Badge className="absolute -top-2 right-2 bg-blue-500 text-slate-900">
                        Popular
                      </Badge>
                    )}
                    <div className="space-y-2">
                      <h4 className="font-medium text-slate-100">{pkg.name}</h4>
                      <div className="flex items-baseline">
                        <span className="text-2xl font-bold text-slate-50">{pkg.credits}</span>
                        <span className="text-sm text-slate-400 ml-1">credits</span>
                      </div>
                      <div className="text-sm text-slate-300">${pkg.price.toFixed(2)}</div>
                      {pkg.description && (
                        <p className="text-xs text-slate-400">{pkg.description}</p>
                      )}
                    </div>
                    {selectedPackage === pkg.id && (
                      <div className="absolute top-2 right-2 h-5 w-5 flex items-center justify-center rounded-full bg-blue-500 text-slate-900">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <Separator className="my-4 bg-slate-700" />
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium mb-2 text-slate-200">Custom Package</h3>
                  <p className="text-sm text-slate-400 mb-4">
                    Create a custom credit package with the exact amount you need.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 w-full">
                    <div className="text-sm font-medium mb-2 text-slate-300">Custom Credit Amount</div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center flex-1">
                        <Input
                          type="number"
                          min="10"
                          max="10000"
                          value={customCredits}
                          onChange={(e) => setCustomCredits(Number(e.target.value))}
                          className="flex-1 bg-slate-700 border-slate-600 text-slate-50"
                        />
                        <span className="ml-2 text-slate-300">= ${(customCredits * 0.1).toFixed(2)}</span>
                      </div>
                      <div className="w-full sm:w-auto">
                        <Button
                          className="w-full sm:w-auto bg-slate-700 text-white border border-slate-600 hover:bg-slate-600 hover:border-slate-500"
                          onClick={() => setSelectedPackage('custom')}
                        >
                          Select Custom Amount
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="history" className="space-y-6">
            <div className="rounded-md border border-slate-700">
              <div className="grid grid-cols-5 bg-slate-700/50 py-3 px-4 text-sm font-medium text-slate-300">
                <div>Date</div>
                <div className="col-span-2">Description</div>
                <div className="text-right">Credits</div>
                <div className="text-right">Amount</div>
              </div>
              {transactions.map((transaction) => (
                <div key={transaction.id} className="grid grid-cols-5 py-3 px-4 text-sm border-t border-slate-700 text-slate-200">
                  <div className="text-slate-400">
                    {new Date(transaction.date).toLocaleDateString()}
                  </div>
                  <div className="col-span-2">
                    {transaction.type === 'Purchase' ? (
                      <span className="flex items-center">
                        <CreditCard className="h-4 w-4 mr-1 text-green-400" />
                        Credit Purchase
                      </span>
                    ) : (
                      <span>
                        {transaction.details}
                      </span>
                    )}
                  </div>
                  <div className={`text-right ${transaction.amount > 0 ? 'text-green-400' : 'text-slate-200'}`}>
                    {transaction.amount > 0 ? `+${transaction.amount}` : transaction.amount}
                  </div>
                  <div className="text-right text-slate-200">
                    {transaction.price ? `$${transaction.price.toFixed(2)}` : '-'}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="rounded-md bg-slate-800/30 p-4 flex items-start gap-2">
              <Info className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-400">
                Credits are consumed when using AI-powered features like strategy optimization, 
                custom analysis, and advanced backtesting. Credits do not expire and can be used 
                at any time.
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
      
      {activeTab === 'purchase' && (
        <CardFooter className="border-t bg-slate-800/50 px-6 py-4 justify-between border-slate-700">
          <div className="text-sm text-slate-300">
            {selectedPackage ? (
              <div className="font-medium">
                {selectedPackage === 'custom' 
                  ? `${customCredits} credits for $${(customCredits * 0.1).toFixed(2)}`
                  : `${creditPackages.find(p => p.id === selectedPackage)?.credits} credits for $${creditPackages.find(p => p.id === selectedPackage)?.price.toFixed(2)}`
                }
              </div>
            ) : (
              <div className="text-slate-400">Select a package to continue</div>
            )}
          </div>
          <Button 
            onClick={() => setShowConfirmation(true)} 
            disabled={!selectedPackage || isPurchasing}
            className="bg-blue-600 hover:bg-blue-700 text-slate-50"
          >
            {isPurchasing ? 'Processing...' : 'Purchase Credits'}
          </Button>
        </CardFooter>
      )}

      {/* Purchase Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="sm:max-w-[500px] bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-100">
              <Shield className="h-5 w-5 text-blue-500" />
              Confirm Credit Purchase
            </DialogTitle>
          </DialogHeader>
          
          {orderDetails && (
            <div className="space-y-6">
              {/* Order Summary */}
              <div className="rounded-lg border border-slate-700 p-4 bg-slate-800/30">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-slate-200">Order Summary</h4>
                  <Badge variant="secondary" className="bg-slate-700 text-slate-200">
                    {orderDetails.name}
                  </Badge>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Coins className="h-4 w-4 text-blue-500" />
                      <span className="text-sm text-slate-300">Credits</span>
                    </div>
                    <span className="font-medium text-slate-100">
                      {orderDetails.credits.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-slate-300">Price</span>
                    </div>
                    <span className="font-medium text-slate-100">
                      ${orderDetails.price.toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-slate-400 pt-2 border-t border-slate-700">
                    <span>Price per credit</span>
                    <span>${(orderDetails.price / orderDetails.credits).toFixed(3)}</span>
                  </div>
                </div>
              </div>

              {/* Current Balance */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-blue-950/30 border border-blue-800/30">
                <div>
                  <div className="text-sm font-medium text-slate-200">Current Balance</div>
                  <div className="text-xs text-slate-400">
                    After purchase: {(credits + orderDetails.credits).toLocaleString()} credits
                  </div>
                </div>
                <div className="text-lg font-bold text-slate-100">
                  {credits.toLocaleString()}
                </div>
              </div>

              {/* Important Notice */}
              <div className="rounded-lg bg-amber-950/20 border border-amber-800/30 p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Development Mode Active
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      This is a development purchase simulation. No real payment will be processed. 
                      Credits will be added to your account for testing purposes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmation(false)}
              disabled={isPurchasing}
              className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePurchase}
              disabled={isPurchasing}
              className="dark:bg-blue-600 dark:hover:bg-blue-700 dark:text-slate-50"
            >
              {isPurchasing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Confirm Purchase
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};